/**
 * Invoice Group Utilities
 *
 * Shared grouping logic used by InvoiceTemplateBuilder, InvoicePreview,
 * and InvoiceGenerationWizard Step 2.
 *
 * The single source of truth for how line items are grouped, labelled,
 * and subtotalled. All three consumers import from here — no parallel
 * implementations.
 */

import type { InvoiceLineItem, LineItemType } from '@/lib/invoice-template-types';
import type { GroupByOption } from '@/lib/invoice-template-types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * A single group of line items produced by `groupLineItems`.
 *
 * - `groupKey`       — the raw field value used to bucket items (e.g. "Sarah Johnson")
 * - `groupLabel`     — the resolved display string for the group header row
 * - `items`          — the line items that belong to this group
 * - `subtotalAmount` — sum of `item.amount` across all items in the group
 * - `totalQty`       — sum of `item.qty` across all items in the group
 * - `itemCount`      — number of line items in the group
 */
export type GroupedLineItems = {
  groupKey: string;
  groupLabel: string;
  items: InvoiceLineItem[];
  subtotalAmount: number;
  totalQty: number;
  itemCount: number;
};

// ============================================================================
// GROUPING MAP
// ============================================================================

/**
 * Describes how each GroupByOption maps to a line item field and a default
 * label template.
 *
 * PRD Section 9 — Workstream C, Grouping map table.
 */
const GROUP_BY_CONFIG: Record<
  Exclude<GroupByOption, 'none'>,
  {
    /** The field on InvoiceLineItem used as the group key */
    keyField: keyof InvoiceLineItem;
    /** Default label template when no groupHeaderLabel is provided */
    defaultLabelTemplate: string;
  }
> = {
  person: {
    keyField: 'person',
    defaultLabelTemplate: '{person}',
  },
  role: {
    keyField: 'role',
    defaultLabelTemplate: '{role}',
  },
  phase: {
    keyField: 'phase',
    defaultLabelTemplate: '{phase}',
  },
  task: {
    keyField: 'task',
    defaultLabelTemplate: '{task}',
  },
  'cost-center': {
    keyField: 'costCentreName',
    defaultLabelTemplate: '{costCentreName}',
  },
};

// ============================================================================
// RESOLVE GROUP LABEL
// ============================================================================

/**
 * Resolves a group header label template string against the data of the
 * first item in the group.
 *
 * Supported tokens (replaced with the corresponding field value):
 *
 *   Contextual (item-level, always available):
 *   {person}          → item.person
 *   {role}            → item.role
 *   {phase}           → item.phase
 *   {task}            → item.task
 *   {costCentreName}  → item.costCentreName
 *   {costCentreCode}  → item.costCentreCode
 *
 *   Aggregate (group-level, resolved when groupAggregates is provided):
 *   {qty}             → groupAggregates.totalQty  (2 decimal places)
 *   {subtotal}        → groupAggregates.subtotalAmount  (2 decimal places)
 *   {itemCount}       → groupAggregates.itemCount
 *
 * Unknown tokens are left as-is so they are visible in the UI.
 *
 * Multi-value safety: when items in the group have different values for a
 * field token (e.g. different cost centres within a person-grouped invoice),
 * all distinct values are joined with ", " so the output is never silently
 * wrong.  If every item shares the same value the output is identical to
 * the old single-item behaviour.
 *
 * @param template        - The label template string, e.g. "{person} — {role}"
 * @param groupItems      - All line items that belong to the group
 * @param groupAggregates - Optional group-level aggregates (totalQty, subtotalAmount, itemCount)
 * @returns               - The resolved label string
 *
 * PRD Section 10 — Workstream D, resolveGroupLabel.
 */
export function resolveGroupLabel(
  template: string,
  groupItems: InvoiceLineItem[],
  groupAggregates?: { totalQty: number; subtotalAmount: number; itemCount: number },
): string {
  /** Collects all distinct non-blank values for a field across the group. */
  function distinctValues(field: keyof InvoiceLineItem): string {
    const seen = new Set<string>();
    for (const item of groupItems) {
      const v = String(item[field] ?? '').trim();
      if (v) seen.add(v);
    }
    return Array.from(seen).join(', ');
  }

  let result = template
    .replace(/\{person\}/g,         distinctValues('person'))
    .replace(/\{role\}/g,           distinctValues('role'))
    .replace(/\{phase\}/g,          distinctValues('phase'))
    .replace(/\{task\}/g,           distinctValues('task'))
    .replace(/\{costCentreName\}/g, distinctValues('costCentreName'))
    .replace(/\{costCentreCode\}/g, distinctValues('costCentreCode'));

  if (groupAggregates) {
    result = result
      .replace(/\{qty\}/g,       groupAggregates.totalQty.toFixed(2))
      .replace(/\{subtotal\}/g,  groupAggregates.subtotalAmount.toFixed(2))
      .replace(/\{itemCount\}/g, String(groupAggregates.itemCount));
  }

  return result;
}

// ============================================================================
// GROUP LINE ITEMS
// ============================================================================

/**
 * Groups an array of line items by the specified `groupBy` field.
 *
 * Returns `null` when `groupBy === 'none'` — callers should render a flat
 * table in that case.
 *
 * Returns an ordered array of `GroupedLineItems` otherwise. Groups appear
 * in the order their first item was encountered in the input array, so the
 * output order is stable and predictable.
 *
 * @param items              - The flat array of line items to group
 * @param groupBy            - Which field to group by (from GroupByOption)
 * @param groupLabelTemplate - Optional custom label template (e.g. "{person} — {role}").
 *                             Falls back to the default template for the chosen groupBy.
 * @returns                  - Ordered array of GroupedLineItems, or null when groupBy is 'none'
 *
 * PRD Section 9 — Workstream C.
 */
export function groupLineItems(
  items: InvoiceLineItem[],
  groupBy: GroupByOption,
  groupLabelTemplate?: string,
): GroupedLineItems[] | null {
  // ── Early exit: no grouping requested ────────────────────────────────────
  if (groupBy === 'none') {
    return null;
  }

  const config = GROUP_BY_CONFIG[groupBy];
  const labelTemplate = groupLabelTemplate ?? config.defaultLabelTemplate;

  // ── Bucket items by group key, preserving insertion order ────────────────
  // Using a Map so that groups appear in the order their first item was seen.
  const buckets = new Map<string, InvoiceLineItem[]>();

  for (const item of items) {
    const rawKey = item[config.keyField];
    // Coerce to string; treat null/undefined/empty as a dedicated "(Unassigned)" group
    const key = rawKey != null && String(rawKey).trim() !== ''
      ? String(rawKey)
      : '(Unassigned)';

    if (!buckets.has(key)) {
      buckets.set(key, []);
    }
    buckets.get(key)!.push(item);
  }

  // ── Build GroupedLineItems array ──────────────────────────────────────────
  const groups: GroupedLineItems[] = [];

  for (const [groupKey, groupItems] of buckets.entries()) {
    // Pass all items so resolveGroupLabel can collect distinct values across
    // the whole group — e.g. a person-grouped row spanning multiple cost
    // centres will show "Marketing, Engineering, HR" rather than silently
    // picking only the first item's cost centre.
    const resolvedLabel =
      groupKey === '(Unassigned)'
        ? '(Unassigned)'
        : resolveGroupLabel(labelTemplate, groupItems);

    const subtotalAmount = groupItems.reduce((sum, item) => sum + (item.amount ?? 0), 0);
    const totalQty = groupItems.reduce((sum, item) => sum + (item.qty ?? 0), 0);

    groups.push({
      groupKey,
      groupLabel: resolvedLabel,
      items: groupItems,
      subtotalAmount: Math.round(subtotalAmount * 100) / 100,
      totalQty: Math.round(totalQty * 100) / 100,
      itemCount: groupItems.length,
    });
  }

  return groups;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Returns the available token strings for a given GroupByOption.
 * Used by the Group Header Label UI (Task 3.8) to show only relevant
 * token chips for the currently selected groupBy.
 *
 * PRD Section 10 — Workstream D, "Available tokens by Group By" table.
 */
export const GROUP_BY_TOKENS: Record<Exclude<GroupByOption, 'none'>, string[]> = {
  person: ['{person}', '{role}'],
  role: ['{role}'],
  phase: ['{phase}'],
  task: ['{task}', '{phase}'],
  'cost-center': ['{costCentreName}', '{costCentreCode}'],
};

/**
 * Common aggregate tokens available regardless of the active groupBy.
 * These resolve against group-level aggregates (totalQty, subtotalAmount, itemCount)
 * rather than individual line item fields.
 *
 * Shown in the second "Also available" chip row in the Grouped Row Description dialogue.
 */
export const GROUP_COMMON_TOKENS: string[] = [
  '{qty}',
  '{subtotal}',
  '{itemCount}',
  '{person}',
  '{role}',
  '{phase}',
  '{task}',
  '{costCentreName}',
  '{costCentreCode}',
];

/**
 * Contextual tokens for EXPENSE grouped row descriptions.
 * Expenses have no person/role — tokens are cost-centre and phase based.
 */
export const EXPENSE_TOKENS: string[] = [
  '{costCentreName}',
  '{costCentreCode}',
  '{phase}',
];

/**
 * Contextual tokens for PURCHASE grouped row descriptions.
 * Purchases have no person/role — tokens are cost-centre based.
 */
export const PURCHASE_TOKENS: string[] = [
  '{costCentreName}',
  '{costCentreCode}',
];

/**
 * Human-readable metadata for every group-description token.
 * Used by the TokenSidePanel to show friendly labels and descriptions
 * alongside each token button.
 */
export interface GroupTokenMeta {
  /** Friendly display name shown in the side panel (no braces) */
  label: string;
  /** One-line description of what this field resolves to */
  description: string;
}

export const GROUP_TOKEN_META: Record<string, GroupTokenMeta> = {
  '{person}':        { label: 'Person',          description: 'Full name of the person who performed the work' },
  '{role}':          { label: 'Role',             description: 'Job role or title of the person (e.g. Senior Developer)' },
  '{phase}':         { label: 'Phase',            description: 'Project phase the work belongs to' },
  '{task}':          { label: 'Task',             description: 'Specific task or work item name' },
  '{costCentreName}':{ label: 'Cost Centre Name', description: 'Name of the cost centre (e.g. Marketing)' },
  '{costCentreCode}':{ label: 'Cost Centre Code', description: 'Short code for the cost centre (e.g. MKT)' },
  '{qty}':           { label: 'Quantity',         description: 'Total hours or units billed in this group' },
  '{subtotal}':      { label: 'Subtotal',         description: 'Total monetary value for this group' },
  '{itemCount}':     { label: 'Item Count',       description: 'Number of individual line items in this group' },
};

/**
 * Infers the dominant LineItemType for a group based on its items.
 * Returns 'timesheet' if any item has a non-blank person (or explicit lineItemType 'timesheet').
 * Returns 'expense' if all items have lineItemType 'expense'.
 * Returns 'purchase' if all items have lineItemType 'purchase'.
 * Falls back to 'timesheet' when lineItemType is absent (backwards-compatibility).
 */
export function inferGroupType(items: InvoiceLineItem[]): LineItemType {
  if (items.length === 0) return 'timesheet';
  // Prefer explicit lineItemType if present on the first item
  const first = items[0];
  if (first.lineItemType) return first.lineItemType;
  // Fallback: infer from presence of person field
  return first.person?.trim() ? 'timesheet' : 'expense';
}

/**
 * Given three optional description templates (timesheet / expense / purchase),
 * picks the correct one for a group based on `inferGroupType`.
 *
 * @param items                        - Items belonging to the group
 * @param timesheetTemplate            - HTML template for timesheet groups
 * @param expenseTemplate              - HTML template for expense groups
 * @param purchaseTemplate             - HTML template for purchase groups
 * @returns                            - The appropriate template string (may be empty)
 */
export function getDescriptionTemplateForGroup(
  items: InvoiceLineItem[],
  timesheetTemplate: string,
  expenseTemplate: string,
  purchaseTemplate: string,
): string {
  const type = inferGroupType(items);
  if (type === 'expense') return expenseTemplate;
  if (type === 'purchase') return purchaseTemplate;
  return timesheetTemplate;
}

/**
 * Returns the default label template for a given GroupByOption.
 * Convenience wrapper used by components that need the default without
 * importing the full GROUP_BY_CONFIG map.
 */
export function getDefaultGroupLabelTemplate(groupBy: Exclude<GroupByOption, 'none'>): string {
  return GROUP_BY_CONFIG[groupBy].defaultLabelTemplate;
}

/**
 * Formats a line item's transaction type for display in the "Type" column.
 *
 * Accepts either the canonical `LineItemType` enum ('timesheet' | 'expense' |
 * 'purchase') or the richer WIP source-type labels surfaced by the app
 * ('Time', 'Expense', 'Purchase', 'Invoice', 'Journal', 'VAT Journal',
 * 'Credit Note', 'Adjustment', etc.). Labels that are already display-ready
 * are returned unchanged; only the lower-cased enum values are title-cased.
 *
 * Falls back to 'Timesheet' when no type is present.
 */
export function formatLineItemTypeLabel(raw?: string | null): string {
  if (!raw) return 'Timesheet';
  switch (raw.toLowerCase()) {
    case 'timesheet': return 'Timesheet';
    case 'time': return 'Time';
    case 'expense': return 'Expense';
    case 'purchase': return 'Purchase';
    default: return raw;
  }
}
